// src/pages/admin/ViewTestimonials.jsx
import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  Paper,
  Box,
  Typography,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Switch,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  LinearProgress,
  Pagination,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  getAllTestimonialsAdmin,
  updateTestimonial,
  deleteTestimonial,
} from "../../services/api";

/**
 * ViewTestimonials.jsx
 *
 * - Lists testimonials in a table with columns: ID, Name, Review, Active, Actions
 * - Actions: Edit (open dialog), Toggle Active (Switch), Delete (soft -> isActive=false)
 * - Supports search and pagination
 *
 * Ensure api exports:
 * getAllTestimonialsAdmin(params)
 * updateTestimonial(id, data)
 * deleteTestimonial(id)
 */

export default function ViewTestimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({ total: 0, limit: 10, offset: 0 });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [snack, setSnack] = useState({ open: false, severity: "info", message: "" });

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  // loadTestimonials - stable identity with useCallback
  const loadTestimonials = useCallback(
    async (opts = {}) => {
      setLoading(true);
      try {
        const limit = opts.limit ?? meta.limit ?? 10;
        const offset = opts.offset ?? (page - 1) * limit;
        const res = await getAllTestimonialsAdmin({
          limit,
          offset,
          search: search || undefined,
          isActive: opts.isActive ?? undefined,
        });
        const data = res?.data?.data || [];
        const m = res?.data?.meta || {};
        setTestimonials(data);
        setMeta({ limit: m.limit ?? limit, offset: m.offset ?? offset, total: m.total ?? data.length });
      } catch (err) {
        console.error("Failed to load testimonials", err);
        setSnack({ open: true, severity: "error", message: "Failed to load testimonials" });
      } finally {
        setLoading(false);
      }
    },
    [meta.limit, page, search]
  );

  useEffect(() => {
    loadTestimonials();
  }, [loadTestimonials]);

  useEffect(() => {
    // reload when page or search changes
    loadTestimonials();
  }, [page, search, loadTestimonials]);

  const handleDelete = async (id) => {
  // dynamic import so Swal is always available at runtime
  let Swal;
  try {
    Swal = (await import("sweetalert2")).default;
  } catch (impErr) {
    console.error("Failed to load sweetalert2", impErr);
    setSnack({ open: true, severity: "error", message: "Failed to load confirmation dialog" });
    return;
  }

  const result = await Swal.fire({
    title: "Are you sure?",
    text: "This testimonial will be deactivated (soft delete).",
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Yes, deactivate",
    cancelButtonText: "Cancel",
    confirmButtonColor: "#d33",
    cancelButtonColor: "#3085d6",
    reverseButtons: true,
    focusCancel: true,
  });

  if (!result.isConfirmed) return;

  // show loading modal while the delete API is in progress
  Swal.fire({
    title: "Deactivating...",
    allowOutsideClick: false,
    didOpen: () => Swal.showLoading(),
  });

  setLoading(true);
  try {
    await deleteTestimonial(id);

    // close loading and show success
    Swal.close();
    await Swal.fire({
      title: "Deactivated",
      text: "Testimonial has been deactivated.",
      icon: "success",
      timer: 1400,
      showConfirmButton: false,
    });

    setSnack({ open: true, severity: "success", message: "Testimonial deactivated" });
    loadTestimonials();
  } catch (err) {
    console.error("deleteTestimonial err", err);
    Swal.close();
    await Swal.fire({
      title: "Error",
      text: err?.response?.data?.message || "Failed to deactivate testimonial.",
      icon: "error",
      confirmButtonColor: "#3085d6",
    });
    setSnack({ open: true, severity: "error", message: "Failed to deactivate testimonial" });
  } finally {
    setLoading(false);
  }
};


  const toggleActive = async (t) => {
    const id = t.id;
    const newVal = !t.isActive;
    // optimistic UI
    setTestimonials((s) => s.map((x) => (x.id === id ? { ...x, isActive: newVal } : x)));
    try {
      await updateTestimonial(id, { isActive: newVal });
      setSnack({ open: true, severity: "success", message: `Testimonial ${newVal ? "activated" : "deactivated"}` });
    } catch (err) {
      console.error("toggleActive err", err);
      setSnack({ open: true, severity: "error", message: "Failed to update status" });
      // revert
      setTestimonials((s) => s.map((x) => (x.id === id ? { ...x, isActive: t.isActive } : x)));
    }
  };

  const openEdit = (t) => {
    setEditing({
      id: t.id,
      name: t.name ?? "",
      review: t.review ?? "",
      isActive: Boolean(t.isActive),
    });
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditing(null);
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    if (!editing.name.trim()) {
      setSnack({ open: true, severity: "error", message: "Name cannot be empty" });
      return;
    }
    if (!editing.review.trim()) {
      setSnack({ open: true, severity: "error", message: "Review cannot be empty" });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: editing.name.trim(),
        review: editing.review.trim(),
        isActive: Boolean(editing.isActive),
      };
      await updateTestimonial(editing.id, payload);
      setSnack({ open: true, severity: "success", message: "Testimonial updated" });
      closeEdit();
      loadTestimonials();
    } catch (err) {
      console.error("updateTestimonial err", err);
      setSnack({ open: true, severity: "error", message: "Failed to update testimonial" });
    } finally {
      setSaving(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil((meta.total || 0) / (meta.limit || 10)));

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6">Testimonials</Typography>

          <Box display="flex" gap={1} alignItems="center">
            <TextField
              size="small"
              placeholder="Search name or review"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
            <Tooltip title="Refresh">
              <IconButton onClick={() => loadTestimonials()}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={80}>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Review</TableCell>
                <TableCell width={120}>Active</TableCell>
                <TableCell align="right" width={160}>Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {testimonials.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">No testimonials found</TableCell>
                </TableRow>
              ) : (
                testimonials.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell>{t.id}</TableCell>
                    <TableCell>{t.name}</TableCell>
                    <TableCell>
                      <Typography noWrap sx={{ maxWidth: 700 }}>{t.review}</Typography>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={Boolean(t.isActive)}
                        onChange={() => toggleActive(t)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(t)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Deactivate">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(t.id)}
                          sx={{ ml: 1 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
          <Typography variant="body2" color="text.secondary">
            Showing {testimonials.length} of {meta.total ?? 0}
          </Typography>

          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            size="small"
          />
        </Box>
      </Paper>

      {/* Edit dialog */}
      <Dialog open={editOpen} onClose={closeEdit} fullWidth maxWidth="md">
        <DialogTitle>Edit Testimonial</DialogTitle>
        <DialogContent>
          {editing && (
            <Box display="flex" flexDirection="column" gap={2} mt={1}>
              <TextField
                label="Name"
                value={editing.name}
                onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Review"
                value={editing.review}
                onChange={(e) => setEditing((s) => ({ ...s, review: e.target.value }))}
                fullWidth
                multiline
                rows={6}
              />
              <Box display="flex" gap={2} alignItems="center">
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel id="active-label">Active</InputLabel>
                  <Select
                    labelId="active-label"
                    value={editing.isActive ? "true" : "false"}
                    label="Active"
                    onChange={(e) => setEditing((s) => ({ ...s, isActive: e.target.value === "true" }))}
                  >
                    <MenuItem value="true">Active</MenuItem>
                    <MenuItem value="false">Inactive</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary">
                  Toggle visibility on website
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={closeEdit} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={saving}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnack((s) => ({ ...s, open: false }))} severity={snack.severity} sx={{ width: "100%" }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
